<script setup>
import { computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useScheduleStore } from '@/stores/schedule.store'
import { countTaskProgress, useTaskActions, useTaskLoader, useTaskServiceOptions } from '@/composables/tasks'

import TaskItem from '@/components/task/TaskItem.vue'
import TaskProgress from '@/components/task/TaskProgress.vue'

const props = defineProps({
  taskIds: {
    type: Array,
    required: true,
  },
})

const i18n = getI18n()
const scheduleStore = useScheduleStore()
const { ensureServicesAsync } = useTaskServiceOptions()

const fetchTasksAsync = async() => {
  const loaded = await scheduleStore.fetchTasksAsync({ ids: props.taskIds.join(',') })
  const byId = Object.fromEntries(loaded.map(task => [task.id, task]))
  return props.taskIds.map(id => byId[id]).filter(Boolean)
}

const { tasks, loadAsync } = useTaskLoader(fetchTasksAsync)
const progress = computed(() => countTaskProgress(tasks.value))
const { toggleCompletedAsync } = useTaskActions()

onMounted(() => Promise.all([scheduleStore.ensureTaskTypesAsync(), ensureServicesAsync(), loadAsync()]))

</script>

<template>
  <q-card
    v-if="tasks.length"
    flat
    bordered
    class="q-mt-sm"
  >
    <q-item dense class="q-pa-sm">
      <q-item-section avatar>
        <q-icon name="mdi-checkbox-marked-circle-outline" color="primary" />
      </q-item-section>
      <q-item-section>
        <q-item-label class="text-weight-medium">{{ i18n.t('common.tasks.tab') }}</q-item-label>
      </q-item-section>
      <q-item-section side>
        <q-item-label caption>{{ progress.done }}/{{ progress.total }}</q-item-label>
      </q-item-section>
    </q-item>
    <task-progress :progress="progress" />
    <q-list dense>
      <task-item
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        show-container
        show-type
        :openable="false"
        @toggle="toggleCompletedAsync"
      />
    </q-list>
  </q-card>
</template>
